<template>
  <v-dialog
    v-if="selected"
    :value="dialog"
    :hide-overlay="!dialog"
    @click:outside.stop="closeDialog(true)"
    @keydown.esc="closeDialog(true)"
    persistent
    max-width="625px"
    :fullscreen="is_mobile"
    :scrollable="is_mobile"
    overlay-opacity="50"
    overlay-color="#ffffff"
    content-class="default-dialog"
    >
    <v-card class="default-dialog-card">
      <v-card-title class="dialog-title">
        <span class="headline">
          <v-icon class="toolbar-icon">{{ (id > 0 ? 'mdi-account-edit' : 'mdi-account-plus') }}</v-icon>
          {{ (id > 0 ? `${lang.users.edit.title} #${id}` : lang.users.new.title) }}
        </span>
        <v-spacer></v-spacer>
        <v-icon class="dialog-close-btn" @click="closeDialog(true)">mdi-close</v-icon>
      </v-card-title>
      <v-card-text>
        <v-container class="pa-0 pt-4">
          <v-form
            ref="SaveForm"
            v-model="validate.valid"
            lazy-validation
            class="user-form"
          >
            <v-row class="pa-0 ma-0">
              <v-col cols="4" lg="3" md="3" sm="4" class="pa-0">
                <Avatar
                  :photo="selected.avatar"
                  :size="129"
                  :edit="true"
                  @changed="imageChanged"
                />
              </v-col>
              <v-col cols="8" lg="9" md="9" sm="8" class="pa-0">
                <v-row class="user-form-row">
                  <v-col cols="6">
                    <v-text-field
                      :label="lang.users.datatable.headers.name"
                      v-model="selected.name"
                      :rules="lang.users.edit.rules.nameRules"
                      :loading="get_interface.loading"
                      :disabled="get_interface.loading"
                      autocomplete="new-password"
                      filled
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :label="lang.users.datatable.headers.login"
                      v-model="selected.login"
                      :rules="lang.users.edit.rules.loginRules"
                      :error="validate.login_exist"
                      :error-messages="validate.login_exist_msg"
                      :loading="get_interface.loading"
                      :disabled="get_interface.loading"
                      autocomplete="new-password"
                      filled
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="user-form-row">
                  <v-col cols="6">
                    <v-text-field
                      :label="lang.users.edit.newpass"
                      v-model="selected.pass"
                      :rules="(id > 0 ? lang.users.edit.rules.passRules : lang.users.new.rules.passRules)"
                      :error="validate.match_error"
                      :error-messages="validate.match_error_msg"
                      :append-icon="show_pass1 ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="show_pass1 ? 'text' : 'password'"
                      :loading="get_interface.loading"
                      :disabled="get_interface.loading"
                      @click:append="show_pass1 = !show_pass1"
                      autocomplete="new-password"
                      filled
                    ></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :label="lang.users.edit.newpass_confirm"
                      v-model="selected.pass_confirm"
                      :rules="(id > 0 ? lang.users.edit.rules.passRules : lang.users.new.rules.passRules)"
                      :error="validate.match_error"
                      :error-messages="validate.match_error_msg"
                      :append-icon="show_pass2 ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="show_pass2 ? 'text' : 'password'"
                      :loading="get_interface.loading"
                      :disabled="get_interface.loading"
                      @click:append="show_pass2 = !show_pass2"
                      autocomplete="new-password"
                      filled
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="user-form-row">
              <v-col cols="6">
                <v-select
                  :items="get_data.users_permissions"
                  :label="lang.users.datatable.headers.function"
                  :rules="lang.users.new.rules.functionRules"
                  v-model="selected.function_id"
                  :loading="get_interface.loading"
                  :disabled="get_interface.loading"
                  item-value="id"
                  item-text="name"
                  filled
                >
                  <template v-slot:selection="{ selected, item }">
                    <v-chip
                      :input-value="selected"
                      class="chip--select-multi white--text"
                      :color="item.color"
                    >
                      {{ item.name }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title :class="`${item.color}--text`">
                        {{ item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="6">
                <v-row class="pa-0 ma-0">
                  <v-col cols="12" class="pa-0 ma-0">
                    <treeselect
                      :options="categories"
                      v-model="selected.cat"
                      :multiple="true"
                      :placeholder="lang.users.datatable.headers.all_category"
                      :noChildrenText="lang.items.new.noChildrenText"
                      :noOptionsText="lang.items.new.noCategoriesText"
                      :noResultsText="lang.items.new.noResultsText"
                      :clearable="true"
                      :default-expand-level="2"
                      valueConsistsOf="LEAF_PRIORITY"
                      :zIndex="9999"
                      :disabled="get_interface.loading || Boolean(selected.no_cat)"
                      appendToBody
                      search-nested
                    />
                  </v-col>
                </v-row>
                <v-row class="pa-0 ma-0">
                  <v-col cols="12" class="pa-0 ma-0">
                    <v-checkbox
                      v-model="selected.no_cat"
                      :label="lang.users.new.no_cat"
                      :disabled="get_interface.loading"
                      color="primary"
                      class="small-checkbox ma-0 mt-1 mb-2"
                      hide-details
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="gray" text @click="closeDialog(true)">{{ lang.users.view.close }}</v-btn>
        <v-btn color="red darken-1" v-if="id > 0" text @click="deleteUser(selected)">{{ lang.users.actions.delete }}</v-btn>
        <v-btn
          color="green darken-1"
          text
          @click="saveUser(selected)"
          :loading="get_interface.loading"
          :disabled="get_interface.loading"
        >
          {{ lang.users.actions.save }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Avatar from '@/components/users/templates/Avatar'

import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

import appdata from '@/mixins/appdata'
import utils from '@/mixins/utils'
import save from '@/mixins/defaults/save'
import users from './mixins/index'

export default {
  components: { Avatar, Treeselect },
  mixins: [ appdata, utils, save, users ],
  data () {
    return {
      validate: {
        valid: false,
        match_error: false,
        match_error_msg: '',
        login_exist: false,
        login_exist_msg: ''
      },
      show_pass1: false,
      show_pass2: false
    }
  },
  computed: {
    categories: function () {
      var r = {}
      var categories = Object.values(this.get_data.categories) || []
      var parents = categories.filter(cat => cat.parent === 0) || []
      parents.forEach(function (val) {
        val.label = val.name
        const childrens = categories.filter(cat => cat.parent !== 0 && cat.parent === val.id) || []
        childrens.forEach(function (childVal) {
          childVal.label = childVal.name
        })
        if (childrens.length > 0) val.children = childrens
      })
      parents.forEach(function (a) {
        let obj = {
          id: a.catalog,
          label: this.lang[a.catalog].title
        }
        if (typeof r[a.catalog] !== 'undefined') {
          obj.children = [ ...r[a.catalog].children || [], a ]
          r[a.catalog] = obj
        } else {
          obj.children = [ a ]
          r[a.catalog] = Object.assign({}, { ...obj })
        }
      }.bind(this))
      //console.log('result', r)
      return Object.values(r)
    }
  },
  methods: {
    imageChanged (image) {
      this.selected.avatar = image
    }
  }
}
</script>

<style src="./styles/save.scss" lang="scss"></style>
